import { useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';
import { api } from '../../api/client';
import { KPICard, TimeSeriesChart, DataTable } from '../../components/intelligence';
import { FilterBar, createFilterState, filtersToParams } from '../../components/intelligence/FilterBar';
import { SkeletonKPI, SkeletonChart, SkeletonTable } from '../../components/intelligence/SkeletonLoader';

import { T } from '../../styles/tokens';
interface LLMStats {
  total_requests: number;
  total_tokens_in: number;
  total_tokens_out: number;
  total_cost: number;
  by_model: Array<{ model: string; requests: number; tokens_in: number; tokens_out: number; cost: number }>;
  by_route: Array<{ route: string; requests: number; cost: number }>;
  top_users: Array<{ user: string; requests: number; cost: number }>;
  daily: Array<{ date: string; cost: number; requests: number }>;
}

const loading = signal(true);
const error = signal('');
const stats = signal<LLMStats | null>(null);
const filters = createFilterState();

async function loadData() {
  loading.value = true;
  error.value = '';
  try {
    const params = filtersToParams(filters.value);
    const res = await api.get<any>(`/intelligence/aggregates?metric=llm&${params.toString()}`);
    const agg = res.aggregates || res;
    stats.value = {
      total_requests: agg.llm_requests || 0,
      total_tokens_in: agg.llm_tokens_in || agg.llm_tokens || 0,
      total_tokens_out: agg.llm_tokens_out || 0,
      total_cost: agg.llm_cost || 0,
      by_model: agg.llm_by_model || [],
      by_route: agg.llm_by_route || [],
      top_users: agg.llm_top_users || [],
      daily: agg.llm_daily || agg.daily_events?.map((d: any) => ({ date: d.date, cost: 0, requests: d.count })) || [],
    };
  } catch (err: any) {
    error.value = err.message || 'Erro ao carregar custos LLM';
    stats.value = { total_requests: 0, total_tokens_in: 0, total_tokens_out: 0, total_cost: 0, by_model: [], by_route: [], top_users: [], daily: [] };
  } finally {
    loading.value = false;
  }
}

export function LLMCostsPanel() {
  useEffect(() => { loadData(); }, []);

  const d = stats.value;

  return (
    <div>
 <h2 class="text-xl font-bold text-white mb-4">Custos LLM</h2>
      <FilterBar filters={filters} onApply={loadData} showTool={false} />

      {error.value && (
        <div class="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg p-3 mb-4 text-sm">{error.value}</div>
      )}

      {/* KPIs */}
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {loading.value ? (
          Array.from({ length: 4 }).map((_, i) => <SkeletonKPI key={i} />)
        ) : (
          <>
 <KPICard icon="" label="Total Requests" value={d?.total_requests || 0} />
 <KPICard icon="" label="Tokens In" value={(d?.total_tokens_in || 0).toLocaleString()} />
 <KPICard icon="" label="Tokens Out" value={(d?.total_tokens_out || 0).toLocaleString()} />
 <KPICard icon="" label="Custo Total" value={`$${(d?.total_cost || 0).toFixed(2)}`} />
          </>
        )}
      </div>

      {/* Cost over time */}
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {loading.value ? (
          <>
            <SkeletonChart />
            <SkeletonChart />
          </>
        ) : (
          <>
            <div class="[&>div]:!bg-gray-800 [&>div]:!border-gray-700">
              <TimeSeriesChart
                title="Custo Diário ($)"
                data={(d?.daily || []).map((e) => ({ label: e.date, value: e.cost }))}
                color={T.warning}
              />
            </div>
            <div class="[&>div]:!bg-gray-800 [&>div]:!border-gray-700">
              <TimeSeriesChart
                title="Requests Diários"
                data={(d?.daily || []).map((e) => ({ label: e.date, value: e.requests }))}
                color="#6366f1"
              />
            </div>
          </>
        )}
      </div>

      {/* By Model */}
      <div class="mb-6">
        <h3 class="text-sm font-medium text-gray-300 mb-3">Por Modelo</h3>
        {loading.value ? <SkeletonTable /> : (
          <div class="[&>div]:!bg-gray-800 [&>div]:!border-gray-700 [&_th]:!bg-gray-800 [&_th]:!text-gray-400 [&_td]:!text-gray-300">
            <DataTable
              columns={[
                { key: 'model', label: 'Modelo' },
                { key: 'requests', label: 'Requests', render: (r: any) => r.requests.toLocaleString() },
                { key: 'tokens_in', label: 'Tokens In', render: (r: any) => r.tokens_in.toLocaleString() },
                { key: 'tokens_out', label: 'Tokens Out', render: (r: any) => r.tokens_out.toLocaleString() },
                { key: 'cost', label: 'Custo', render: (r: any) => `$${r.cost.toFixed(4)}` },
              ]}
              data={d?.by_model || []}
              emptyMessage="Sem dados por modelo"
            />
          </div>
        )}
      </div>

      {/* Top Users */}
      <div>
        <h3 class="text-sm font-medium text-gray-300 mb-3">Top Usuários</h3>
        {loading.value ? <SkeletonTable rows={3} /> : (
          <div class="[&>div]:!bg-gray-800 [&>div]:!border-gray-700 [&_th]:!bg-gray-800 [&_th]:!text-gray-400 [&_td]:!text-gray-300">
            <DataTable
              columns={[
                { key: 'user', label: 'Usuário' },
                { key: 'requests', label: 'Requests', render: (r: any) => r.requests.toLocaleString() },
                { key: 'cost', label: 'Custo', render: (r: any) => `$${r.cost.toFixed(4)}` },
              ]}
              data={d?.top_users || []}
              pageSize={10}
              emptyMessage="Sem dados de usuários"
            />
          </div>
        )}
      </div>
    </div>
  );
}
